import { useEffect, useState } from 'react';
import { Flame, Thermometer } from 'lucide-react';
import api from '@/shared/api/client';
import { horodatageTexte } from '@/shared/utils/liveness';
import CourbeTemperaturePac from '@/features/assets/components/CourbeTemperaturePac';

/**
 * Le relevé d'une pompe à chaleur, lu sur son thermostat connecté (Tuya).
 *
 * POURQUOI CE PANNEAU EXISTE (vidéo de David, 01/10)
 *
 * Un clic sur une batterie ou une centrale PV ouvrait tout son détail ; sur une PAC,
 * rien d'autre que l'identité. Les douze grandeurs étaient pourtant relevées toutes les
 * deux minutes et l'historique écrit toutes les six : ce panneau les montre.
 *
 * ⚠️ CE QUE LE THERMOSTAT NE MESURE PAS, ON NE L'AFFICHE PAS COMME UNE VALEUR.
 * La consommation électrique n'existe pas sur ce matériel. « inconnu » laissait croire
 * à une panne de relevé ; « non mesurée par ce thermostat » dit la vérité.
 */
const ETAT_CHAUFFE = {
  heating: 'en chauffe', no_heating: 'à l’arrêt (consigne atteinte)',
};
const MODE = { heat: 'chauffage', cold: 'refroidissement', auto: 'automatique' };
const PROGRAMMATION = { manual: 'manuelle', auto: 'programme hebdomadaire' };

function degres(v) {
  return v == null ? '-' : `${Number(v).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} °C`;
}

function oui(v) {
  if (v == null) return '-';
  return v ? 'activé' : 'désactivé';
}

export default function PacReadout({ asset: brut }) {
  // Le Monitoring rend la traduction du relevé à plat ; le Pilotage la range sous
  // `monitoring` (même `enrich_asset`, autre forme). On lit les deux plutôt que de
  // retraduire ici les datapoints Tuya : une seconde traduction finirait par diverger.
  const asset = { ...(brut.monitoring || {}), ...brut };
  // Daté par le RELEVÉ Tuya et par rien d'autre. `dernier_contact` est simulé « à
  // l'instant » sur les PAC de démonstration : l'afficher ici ferait dire « Relevé à
  // 16:33 » à un thermostat que personne n'a jamais interrogé.
  const releve = asset.releve_tuya?.vu_a || null;
  const lignes = [
    ['Marche', asset.etat || '-'],
    ['Mode', MODE[asset.mode] || asset.mode || '-'],
    ['Fonctionnement', ETAT_CHAUFFE[asset.etat_chauffe] || asset.etat_chauffe || '-'],
    ['Température mesurée', degres(asset.temperature_mesuree)],
    ['Consigne', degres(asset.temperature_consigne)],
    ['Plage autorisée', asset.consigne_min != null || asset.consigne_max != null
      ? `${degres(asset.consigne_min)} à ${degres(asset.consigne_max)}` : '-'],
    ['Correction de sonde', degres(asset.correction_sonde)],
    ['Hors-gel', oui(asset.antigel)],
    ['Verrou enfant', oui(asset.verrou_enfant)],
    ['Programmation', PROGRAMMATION[asset.mode_programmation] || asset.mode_programmation || '-'],
    ['Défaut', asset.defaut == null ? '-' : (Number(asset.defaut) === 0 ? 'aucun' : `code ${asset.defaut}`)],
    ['Consommation', 'non mesurée par ce thermostat'],
  ];

  const [points, setPoints] = useState([]);

  useEffect(() => {
    let vivant = true;
    api.get(`/monitoring/assets/${asset.id}/metriques`, { params: { depuis_h: 24 } })
      .then(r => { if (vivant) setPoints(r.data?.points || []); })
      .catch(() => { if (vivant) setPoints([]); });
    return () => { vivant = false; };
  }, [asset.id, releve]);


  return (
    <div className="p-4 rounded-lg border border-orange-200/80 bg-orange-50/30 space-y-3" data-testid="pac-readout">
      <div className="flex items-center justify-between">
        <h3 className="text-[13px] font-medium text-neutral-900 flex items-center gap-1.5">
          <Flame className="w-3.5 h-3.5 text-orange-500" /> Pompe à chaleur
        </h3>
        <span className="text-[10px] text-neutral-400" data-testid="pac-releve">
          {releve ? `Relevé ${horodatageTexte(releve)}` : 'Jamais relevé'}
        </span>
      </div>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-[12px]">
        {lignes.map(([libelle, valeur]) => (
          <div key={libelle} className="contents">
            <dt className="text-neutral-400">{libelle}</dt>
            <dd className="text-right font-medium text-neutral-800">{valeur}</dd>
          </div>
        ))}
      </dl>
      <div className="pt-2 border-t border-orange-100">
        <p className="text-[10px] uppercase tracking-wide text-neutral-400 mb-1 flex items-center gap-1">
          <Thermometer className="w-3 h-3" /> Température, 24 dernières heures
        </p>
        <CourbeTemperaturePac points={points} />
      </div>
    </div>
  );
}
